import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, Form, Modal, Row, Tag, Tooltip, TreeSelect } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import connectionLinkApi from '../../../../../../api/ConnectionLinkApi';
import folderApi from '../../../../../../api/FolderApi';
import SelectProjectComponent from '../../../../../../components/SelectProjectComponent/SelectProjectComponent';
import useProjects from '../../../../../../hooks/ProjectsHook';
import { FolderDataNode } from '../../../../../../model/elements';
import { Connection, ConnectionLink, ConnectionLinkTarget } from '../../../../../../model/entities';
import notificationService from '../../../../../../services/NotificationService';
import treeFolderService from '../../../../../../services/TreeFolderService';
import ConnectionLinkFolderModal from '../../../../ConnectionsPage/ConnectionPage/ConnectionLinksComponent/ConnectionLinkFolderModal/ConnectionLinkFolderModal';
import ConnectionLinkModalTargets from '../../../../ConnectionsPage/ConnectionPage/ConnectionLinksComponent/ConnectionLinkModal/ConnectionLinkModalTargets/ConnectionLinkModalTargets';
import styles from './ConnectionPermissionLinkModal.module.scss';

/**
 * Returns the connection permission link modal.
 * @returns the connection permission link modal.
 */
const ConnectionPermissionLinkModal = (props: Props): React.ReactElement => {
    const { connectionPermission, onCancel, onSave, connectionLinkId, visible } = props;
    /*** HOOKS ***/

    const intl = useIntl();
    const [form] = Form.useForm();
    const [listProjects, getProject] = useProjects();
    const [connectionLink, setConnectionLink] = useState<ConnectionLink>();
    const [sourceFolderNodes, setSourceFolderNodes] = useState<FolderDataNode[]>([]);
    const [sourceFolderNode, setSourceFolderNode] = useState<FolderDataNode>();
    const [targets, setTargets] = useState<ConnectionLinkTarget[]>([]);
    const [connectionLinkFolderModalVisible, setConnectionLinkFolderModalVisible] = useState<boolean>();
    const [saving, setSaving] = useState<boolean>();
    const [loadingProjects, setLoadingProjects] = useState<boolean>();
    const [loadingFolders, setLoadingFolders] = useState<boolean>();

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoadingProjects(true);
                if (connectionLinkId) {
                    // get connection link, source folders and build folder nodes
                    const responses = await Promise.all([connectionLinkApi.get(connectionLinkId)]);
                    const connectionLink = responses[0];
                    const sourceFolders = await folderApi.list(
                        connectionPermission.source!.provider,
                        connectionPermission.source!.id!,
                        connectionLink.source!.project!.id!,
                    );
                    const sourceFolderNodes = sourceFolders.map((f) => treeFolderService.createFolderNode(f));

                    // get source folder node (currently selected)
                    let sourceFolderNode = sourceFolderNodes.find(
                        (f) => f.folder.id === connectionLink.source?.folder?.id,
                    );
                    if (!sourceFolderNode) {
                        try {
                            const sourceFolder = await folderApi.get(
                                connectionLink.source!.folder!.id!,
                                connectionPermission.source!.provider,
                                connectionPermission.source!.id!,
                                connectionLink.source!.project!.id!,
                            );
                            sourceFolderNode = treeFolderService.createFolderNode(sourceFolder);
                        } catch (error) {
                            // folder has been deleted
                        }
                    }

                    // get targets (include uuid for editing)
                    const targets = connectionLink.targets.map((t) => Object.assign({}, t, { uuid: uuidv4() }));
                    setConnectionLink(connectionLink);
                    setTargets(targets);
                    setSourceFolderNodes(sourceFolderNodes);
                    setSourceFolderNode(sourceFolderNode);

                    // load form values
                    const values: any = Object.assign({}, connectionLink);
                    form.setFieldsValue(values);
                    form.setFieldValue(['source', 'folder'], sourceFolderNode?.folder.name);
                } else {
                    const connectionLink: ConnectionLink = {
                        connectionId: connectionPermission.id,
                        source: {
                            includeAllFiles: true,
                            includeSubfolders: false,
                            files: [],
                            extensions: [],
                        },
                        status: 'ENABLED',
                        targets: [],
                    };
                    setConnectionLink(connectionLink);
                    form.setFieldsValue(connectionLink);
                }
            } catch (error) {
                notificationService.displayError(error, intl, [
                    { status: 500, message: 'connectionPermissionLink.status.notFound' },
                ]);
            } finally {
                setLoadingProjects(undefined);
            }
        };
        init();
    }, [
        connectionPermission.id,
        connectionPermission.source,
        connectionPermission.target,
        connectionLinkId,
        form,
        intl,
    ]);

    /*** METHODS ***/

    const listFolders = async (projectId?: string) => {
        try {
            setLoadingFolders(true);

            // reset previous selected folder
            form.resetFields([['source', 'folder']]);

            // load source folders
            const sourceFolders = projectId
                ? await folderApi.list(
                      connectionPermission.source!.provider,
                      connectionPermission.source!.id!,
                      projectId,
                  )
                : [];
            const sourceFolderNodes = sourceFolders.map((f) => treeFolderService.createFolderNode(f));
            setSourceFolderNodes(sourceFolderNodes);

            // load project
            if (connectionLink && connectionLink.source && projectId) {
                const newConnectionLink = Object.assign({}, connectionLink);
                newConnectionLink.source!.project = await getProject(projectId);
                setConnectionLink(newConnectionLink);
            }
        } catch (error) {
            notificationService.displayError(error, intl);
            setSourceFolderNodes([]);
        } finally {
            setLoadingFolders(undefined);
        }
    };

    const cancel = () => {
        form.resetFields();
        onCancel();
    };

    const submit = async () => {
        try {
            const values = await form.validateFields();
            await save(values);
        } catch (error) {}
    };

    const save = async (values: any) => {
        try {
            setSaving(true);

            const sourceProject = await getProject(values.source.project.id);
            const selectedSourceFolderNode =
                (treeFolderService.find(values.source.folder, sourceFolderNodes) as FolderDataNode) || sourceFolderNode;
            const connectionLinkAux: ConnectionLink = Object.assign({}, connectionLink, {
                source: {
                    project: sourceProject,
                    folder: {
                        id: selectedSourceFolderNode?.folder.id,
                        name:
                            connectionLink?.source?.folder?.id === selectedSourceFolderNode?.folder.id
                                ? connectionLink?.source?.folder?.name
                                : selectedSourceFolderNode?.path,
                    },
                    includeAllFiles: false,
                    includeSubfolders: false,
                    maintainFolderStructure: false,
                    files: values.source.files,
                },
                targets: targets,
            });
            connectionLinkAux.id
                ? await connectionLinkApi.update(connectionLinkAux, connectionPermission)
                : await connectionLinkApi.create(connectionLinkAux, connectionPermission);
            form.resetFields();
            onSave();
        } catch (error: any) {
            if (error.response.status === 409 && error.response.data) {
                notificationService.displayError(error, intl, [
                    { status: 409, message: 'connectionPermissionLink.status.duplicate' },
                ]);
            } else {
                notificationService.displayError(error, intl);
            }
        } finally {
            setSaving(false);
        }
    };

    const loadSubfolderNodes = async (folderId: string) => {
        const sourceFolderNodesAux = [...sourceFolderNodes];
        await treeFolderService.loadSubnodes(folderId, sourceFolderNodesAux);
        setSourceFolderNodes(sourceFolderNodesAux);
    };

    const loadSubfolderNodesByPath = async (folderPath: string): Promise<boolean> => {
        let loaded: boolean = false;
        const folderNames = folderPath
            .split('/')
            .map((folderName) => folderName.trim())
            .filter(Boolean);
        const sourceFolderNodesAux = [...sourceFolderNodes];

        let currentFolderNodes = sourceFolderNodesAux;

        // iterate the folder names
        for (let i = 0; i < folderNames.length; i++) {
            // get folder node by title
            const folderName = folderNames[i];
            const folderNode = currentFolderNodes.find((sfn) => sfn.title === folderName);

            // if folder node exists, load its subnodes
            if (folderNode && folderNode.folder.id) {
                const node = await treeFolderService.loadSubnodes(folderNode.folder.id, sourceFolderNodesAux);
                currentFolderNodes = node && node.children ? (node.children as FolderDataNode[]) : [];

                if (node && i === folderNames.length - 1) {
                    setSourceFolderNodes(sourceFolderNodesAux);
                    form.setFieldsValue({ source: { folder: node.key } });
                    loaded = true;
                }
            }
        }

        return loaded;
    };

    const saveTarget = async (target: ConnectionLinkTarget) => {
        let targetsAux = [...targets];
        const storedTarget = targetsAux.find((t) => t.uuid === target.uuid);
        if (storedTarget) {
            storedTarget.folder = target.folder;
            storedTarget.project = target.project;
        } else {
            targetsAux = [target, ...targetsAux];
        }
        setTargets(targetsAux);
    };

    const removeTarget = async (target: ConnectionLinkTarget) => {
        const targetsAux = targets.filter((t) => t.uuid !== target.uuid);
        setTargets(targetsAux);
    };

    const handleGetProject = useCallback(
        (id: string) => getProject(id, connectionPermission.source!.provider, connectionPermission.source!.id!),
        [getProject, connectionPermission.source],
    );

    const handleListProjects = useCallback(
        (searchText: string) =>
            listProjects(
                0,
                5,
                'name',
                true,
                connectionPermission.source!.id!,
                connectionPermission.source!.provider,
                searchText,
            ),
        [listProjects, connectionPermission.source],
    );

    /*** COMPONENTS ***/

    return (
        <Modal
            title={
                <>
                    <FormattedMessage id="connectionPermissionLink.modal.title" />
                    {connectionLink && connectionLink.cloned && (
                        <Tag className={styles.tag} color="orange">
                            <FormattedMessage id="connectionPermissionLink.cloned" />
                        </Tag>
                    )}
                </>
            }
            open={visible}
            onCancel={cancel}
            onOk={submit}
            okButtonProps={{
                loading: saving,
                disabled: !!loadingProjects || loadingFolders || targets.length === 0,
            }}
            cancelButtonProps={{ disabled: saving }}
            width={1200}
        >
            <Form form={form} colon={false} layout="vertical">
                <Row gutter={[28, 0]}>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage id="connectionPermissionLink.source.project" />}
                            name={['source', 'project', 'id']}
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <SelectProjectComponent
                                size="large"
                                showSearch
                                onChange={(projectId) => listFolders(projectId as string)}
                                loading={loadingProjects}
                                disabled={connectionLink && connectionLink.cloned}
                                onList={handleListProjects}
                                onGet={handleGetProject}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label={
                                <div className={`${styles.label} ${styles.action}`}>
                                    <FormattedMessage id="connectionPermissionLink.source.folder" />
                                    <Tooltip
                                        title={<FormattedMessage id="connectionPermissionLink.source.folder.search" />}
                                    >
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<SearchOutlined />}
                                            onClick={() => setConnectionLinkFolderModalVisible(true)}
                                            disabled={!connectionLink?.source?.project?.id || connectionLink?.cloned}
                                        />
                                    </Tooltip>
                                    <ConnectionLinkFolderModal
                                        onSave={loadSubfolderNodesByPath}
                                        onCancel={() => setConnectionLinkFolderModalVisible(false)}
                                        visible={connectionLinkFolderModalVisible}
                                    />
                                </div>
                            }
                            name={['source', 'folder']}
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <TreeSelect
                                style={{ width: '100%' }}
                                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                treeData={sourceFolderNodes}
                                loadData={(folderNode: any) => loadSubfolderNodes(folderNode.folder.id)}
                                allowClear
                                size="large"
                                loading={loadingProjects || loadingFolders}
                                disabled={connectionLink && connectionLink.cloned}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <ConnectionLinkModalTargets
                connection={connectionPermission}
                targets={targets}
                onSave={saveTarget}
                onRemove={removeTarget}
                loading={loadingProjects}
            />
        </Modal>
    );
};

export default ConnectionPermissionLinkModal;

interface Props {
    visible?: boolean;
    connectionPermission: Connection;
    connectionLinkId?: number;
    onSave: () => Promise<void>;
    onCancel: () => void;
}
